import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'react-hot-toast';
import {AiFillDelete, AiFillEdit} from 'react-icons/ai';
import {NavLink, useNavigate} from 'react-router-dom';
import {useGetQuestionsQuery} from '../api/candidateTest';
import {
  useDeleteProblemMutation,
  useGetCodingQuestionQuery,
} from '../api/codingChallenge';
import AlertModal from '../components/AlertModal';
import CustomTable from '../components/common/CustomTable';
import SubjectDropdown from '../components/common/SubjectDropdown';
import Button from '../components/ui/Button';
import InputField from '../components/ui/InputField';
import {SelectFieldHookForm} from '../components/ui/SelectField';
import {pageRoutes} from '../routes';
import {invalidateQueryV2} from '../services/reactQuery';
import {
  difficultyLevel,
  difficultyLevelOptions,
  userType,
} from '../utils/constants';

const CodingChallenge = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    search: '',
    sort: null,
    sortBy: null,
    page: 1,
    limit: 10,
  });
  const [deleteQuestion, setDeleteQuestion] = useState(null);

  const deleteQuestionMutation = useDeleteProblemMutation();
  const {isLoading: isDeleteProblemLoading} = deleteQuestionMutation;

  const {data: problemData, isFetching: isQuestionsLoading} =
    useGetCodingQuestionQuery(formData);

  const {
    problemData: allQuestionsMain,
    totalItems = null,
    totalPages = null,
  } = problemData?.data || {
    problemData: [],
    totalItems: 0,
    totalPages: 0,
  };

  const allQuestions = problemData?.data.length === 0 ? [] : allQuestionsMain;

  const {handleSubmit, control, watch, register} = useForm({
    mode: 'all',
  });

  const handleFormSubmission = async (values) => {
    const {search, difficulty, subject} = values;
    let payload = {search};

    if (difficulty?.value) payload = {...payload, difficulty: difficulty.value};

    if (subject?.value) payload = {...payload, subject: subject.value};

    // set pagination to page 1
    setFormData((prev) => ({
      ...prev,
      ...payload,
      page: 1,
    }));
  };

  const handleDeleteProblem = (problemId) => {
    // send array of ids which we want to delete
    // that's why passing as array
    deleteQuestionMutation.mutate(problemId, {
      onSuccess: () => {
        setDeleteQuestion(null);
        toast.success('Question successfully removed.');
        invalidateQueryV2(['problem']);
      },
      onError: (data) => toast.error(data.response.data.message),
    });
  };

  useEffect(() => {
    const subscription = watch(handleSubmit(handleFormSubmission));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const handleSort = (sortingDetails) => {
    const {page = null, sortBy, sortDirection = null} = sortingDetails;
    setFormData((prevFormData) => ({
      ...prevFormData,
      page: page || prevFormData.page,
      sortBy,
      sort: sortDirection,
    }));
  };

  const tHeadData = [
    {
      id: 1,
      keyIndex: 'name',
      label: 'Problem name',
      sort: true,
      width: '20%',
    },
    {
      id: 2,
      keyIndex: 'subjects',
      label: 'Subjects',
      render: (subjects) => {
        const as = subjects.map((sub) => {
          return sub.subject;
        });
        return as.join(', ');
      },
      sort: true,
      width: '30%',
    },
    {
      id: 3,
      keyIndex: 'is_this_query',
      label: 'Query/Coding',
      render: (item) => {
        return item ? 'Query' : 'Coding';
      },
      width: '15%',
    },
    {
      id: 4,
      keyIndex: 'duration',
      label: 'Duration (mins)',
      sort: true,
      width: '15%',
    },
    {
      id: 5,
      keyIndex: 'score',
      label: 'Score',
      sort: true,
      width: '10%',
    },
    {
      id: 6,
      keyIndex: '_id',
      label: 'Action',
      width: '10%',
      render: (problemId) => (
        <div className="flex gap-2">
          <NavLink
            aria-label="show candidat details"
            to={`${pageRoutes.editCodingProblem}/${problemId}`}
          >
            <AiFillEdit size={23} />
          </NavLink>
          <button
            type="button"
            aria-label="remove candidate from job"
            onClick={() => setDeleteQuestion(problemId)}
          >
            <AiFillDelete size={20} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <AlertModal
        type="warning"
        isOpen={deleteQuestion}
        onClose={() => setDeleteQuestion(null)}
        title="Confirm this action"
        onCancel={() => setDeleteQuestion(null)}
        onOK={() => handleDeleteProblem(deleteQuestion)}
        okLoading={isDeleteProblemLoading}
      >
        Are you sure you want to delete this question?
      </AlertModal>
      <div className="px-8 py-6">
        <div className="flex items-center gap-2">
          <h3 className="text-2xl font-bold">
            All coding Questions
            {!!Number(totalItems) && `(${totalItems})`}
          </h3>
        </div>
        <form className="py-5">
          <div className="flex justify-between gap-4">
            <div className="gap-4 grid grid-cols-3 self-center">
              <SubjectDropdown
                placeholder="Subject"
                control={control}
                name="subject"
                className="w-full max-w-60 bg-white"
              />

              <SelectFieldHookForm
                placeholder="Difficulty"
                control={control}
                name="difficulty"
                options={difficultyLevelOptions}
                className="w-full max-w-60 bg-white"
              />

              <InputField
                {...register('search')}
                type="search"
                placeholder="Search"
              />
            </div>

            <div className="flex items-center gap-3">
              <Button
                className="py-3 px-5 whitespace-nowrap"
                onClick={() => navigate(pageRoutes.codingProblem)}
              >
                Add questions
              </Button>
            </div>
          </div>
        </form>

        <div className="bg-[white] p-10 border">
          <CustomTable
            tHeadData={tHeadData}
            tBodyData={allQuestions}
            handleSort={handleSort}
            pageCount={totalPages}
            currentPage={formData.page}
            loading={isQuestionsLoading}
          />
        </div>
      </div>
    </>
  );
};

export default CodingChallenge;
